import React, { useState, useEffect } from 'react';
import { superAdminAPI } from '../services/api';

import { useToast } from '../hooks/useToast';
import Icon from './Icon';

function SuperAdminPromotionalApprovals({ darkMode }) {
  const toast = useToast();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending_approval');

  useEffect(() => {
    fetchEmails();
  }, [statusFilter]);

  const fetchEmails = async () => {
    try {
      setLoading(true);
      const response = await superAdminAPI.getPendingPromotionalEmails();
      // Filter based on status
      let filtered = response.data.promotional_emails || [];
      if (statusFilter !== 'all') {
        filtered = filtered.filter(e => e.status === statusFilter);
      }
      setEmails(filtered);
    } catch (err) {
      toast.error('Failed to load promotional emails');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (email) => {
    try {
      const response = await superAdminAPI.getPromotionalEmail(email.id);
      setSelectedEmail({
        ...response.data.promotional_email,
        recipients_count: response.data.recipients_count,
        recipients_preview: response.data.recipients_preview
      });
      setShowDetailModal(true);
    } catch (err) {
      toast.error('Failed to load email details');
    }
  };

  const handleApprove = async () => {
    if (!window.confirm('Are you sure you want to approve and send this promotional email? It will be sent immediately to all recipients.')) return;
    
    try {
      setApproving(true);
      const response = await superAdminAPI.approvePromotionalEmail(selectedEmail.id);
      toast.success(response.data.message);
      setShowDetailModal(false);
      setSelectedEmail(null);
      fetchEmails();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to approve email');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    try {
      setRejecting(true);
      await superAdminAPI.rejectPromotionalEmail(selectedEmail.id, rejectionReason);
      toast.success('Promotional email rejected');
      setShowDetailModal(false);
      setSelectedEmail(null);
      setRejectionReason('');
      fetchEmails();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reject email');
    } finally {
      setRejecting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      draft: { bg: '#6b7280', text: 'white' },
      pending_approval: { bg: '#f59e0b', text: 'white' },
      approved: { bg: '#3b82f6', text: 'white' },
      rejected: { bg: '#ef4444', text: 'white' },
      sent: { bg: '#1A2811', text: 'white' }
    };
    const colors = statusColors[status] || statusColors.draft;
    const labels = {
      draft: 'Draft',
      pending_approval: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
      sent: 'Sent'
    };
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 500,
        backgroundColor: colors.bg,
        color: colors.text
      }}>
        {labels[status] || status}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const getMonthName = (month) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
                   'July', 'August', 'September', 'October', 'November', 'December'];
    return months[month - 1] || '';
  };

  const pendingCount = emails.filter(e => e.status === 'pending_approval').length;

  return (
    <div className="super-admin-promotional-approvals">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ margin: 0, color: darkMode ? '#fff' : '#1a1a2e' }}>
            Promotional Email Approvals
            {pendingCount > 0 && (
              <span style={{
                marginLeft: '12px',
                padding: '4px 10px',
                backgroundColor: '#f59e0b',
                color: 'white',
                borderRadius: '12px',
                fontSize: '14px'
              }}>
                {pendingCount} pending
              </span>
            )}
          </h2>
          <p style={{ margin: '4px 0 0', color: darkMode ? '#a5b4fc' : '#666', fontSize: '14px' }}>
            Review and approve company promotional emails before they are sent
          </p>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: '8px 12px',
            borderRadius: '8px',
            border: `1px solid ${darkMode ? '#4a5568' : '#e5e7eb'}`,
            backgroundColor: darkMode ? '#16213e' : '#fff',
            color: darkMode ? '#fff' : '#1a1a2e'
          }}
        >
          <option value="pending_approval">Pending Approval</option>
          <option value="sent">Sent</option>
          <option value="rejected">Rejected</option>
          <option value="all">All</option>
        </select>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
      ) : emails.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          backgroundColor: darkMode ? '#1a1a2e' : '#f8f9fa',
          borderRadius: '12px'
        }}>
          <Icon name="success" color="muted" darkMode={darkMode} size={48} style={{ marginBottom: '16px' }} />
          <h3 style={{ margin: '0 0 8px', color: darkMode ? '#fff' : '#1a1a2e' }}>
            {statusFilter === 'pending_approval' ? 'No Pending Approvals' : 'No Emails Found'}
          </h3>
          <p style={{ color: darkMode ? '#a5b4fc' : '#666' }}>
            {statusFilter === 'pending_approval' 
              ? 'All caught up! No promotional emails waiting for approval.'
              : 'No promotional emails match your filter.'}
          </p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            backgroundColor: darkMode ? '#1a1a2e' : '#fff',
            borderRadius: '12px',
            overflow: 'hidden'
          }}>
            <thead>
              <tr style={{ backgroundColor: darkMode ? '#16213e' : '#f3f4f6' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: darkMode ? '#a5b4fc' : '#666' }}>Company</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: darkMode ? '#a5b4fc' : '#666' }}>Subject</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: darkMode ? '#a5b4fc' : '#666' }}>Month</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: darkMode ? '#a5b4fc' : '#666' }}>Status</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: darkMode ? '#a5b4fc' : '#666' }}>Submitted</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', color: darkMode ? '#a5b4fc' : '#666' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {emails.map((email) => (
                <tr key={email.id} style={{ borderBottom: `1px solid ${darkMode ? '#2d3748' : '#e5e7eb'}` }}>
                  <td style={{ padding: '12px 16px', color: darkMode ? '#fff' : '#1a1a2e', fontWeight: 500 }}>
                    {email.company_name}
                  </td>
                  <td style={{ padding: '12px 16px', color: darkMode ? '#fff' : '#1a1a2e' }}>
                    {email.subject}
                  </td>
                  <td style={{ padding: '12px 16px', color: darkMode ? '#a5b4fc' : '#666' }}>
                    {getMonthName(email.month)} {email.year}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {getStatusBadge(email.status)}
                  </td>
                  <td style={{ padding: '12px 16px', color: darkMode ? '#a5b4fc' : '#666', fontSize: '13px' }}>
                    {formatDate(email.submitted_at)}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleViewDetail(email)}
                    >
                      {email.status === 'pending_approval' ? 'Review' : 'View'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail/Review Modal - Full Page for better review */}
      {showDetailModal && selectedEmail && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: darkMode ? 'rgba(0,0,0,0.9)' : 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'stretch',
          zIndex: 1000,
          padding: 0
        }}>
          <div style={{
            backgroundColor: darkMode ? '#1a1a2e' : '#fff',
            borderRadius: 0,
            padding: '28px',
            width: '100%',
            maxWidth: '100%',
            height: '100%',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
              <h3 style={{ margin: 0, color: darkMode ? '#fff' : '#1a1a2e', fontSize: '22px' }}>
                {selectedEmail.status === 'pending_approval' ? 'Review Promotional Email' : 'Promotional Email Details'}
              </h3>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedEmail(null);
                  setRejectionReason('');
                }}
                style={{
                  background: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)'
                }}
              >
                ×
              </button>
            </div>
            
            {/* Company Details Section */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '16px', 
              marginBottom: '24px',
              padding: '16px',
              background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
              borderRadius: '12px'
            }}>
              <div>
                <strong style={{ color: darkMode ? '#a5b4fc' : '#666', fontSize: '12px', textTransform: 'uppercase' }}>Company</strong>
                <p style={{ margin: '4px 0 0', color: darkMode ? '#fff' : '#1a1a2e', fontWeight: 500 }}>
                  {selectedEmail.company_name}
                </p>
              </div>
              {selectedEmail.company_email && (
                <div>
                  <strong style={{ color: darkMode ? '#a5b4fc' : '#666', fontSize: '12px', textTransform: 'uppercase' }}>Company Email</strong>
                  <p style={{ margin: '4px 0 0', color: darkMode ? '#fff' : '#1a1a2e' }}>
                    {selectedEmail.company_email}
                  </p>
                </div>
              )}
              {selectedEmail.company_phone && (
                <div>
                  <strong style={{ color: darkMode ? '#a5b4fc' : '#666', fontSize: '12px', textTransform: 'uppercase' }}>Company Phone</strong>
                  <p style={{ margin: '4px 0 0', color: darkMode ? '#fff' : '#1a1a2e' }}>
                    {selectedEmail.company_phone}
                  </p>
                </div>
              )}
              <div>
                <strong style={{ color: darkMode ? '#a5b4fc' : '#666', fontSize: '12px', textTransform: 'uppercase' }}>Status</strong>
                <p style={{ margin: '4px 0 0' }}>{getStatusBadge(selectedEmail.status)}</p>
              </div>
              <div>
                <strong style={{ color: darkMode ? '#a5b4fc' : '#666', fontSize: '12px', textTransform: 'uppercase' }}>Month</strong>
                <p style={{ margin: '4px 0 0', color: darkMode ? '#fff' : '#1a1a2e' }}>
                  {getMonthName(selectedEmail.month)} {selectedEmail.year}
                </p>
              </div>
              <div>
                <strong style={{ color: darkMode ? '#a5b4fc' : '#666', fontSize: '12px', textTransform: 'uppercase' }}>Recipients</strong>
                <p style={{ margin: '4px 0 0', color: darkMode ? '#fff' : '#1a1a2e' }}>
                  {selectedEmail.recipients_count || 0} users
                </p>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <strong style={{ color: darkMode ? '#a5b4fc' : '#666' }}>Subject:</strong>
              <p style={{ margin: '4px 0', color: darkMode ? '#fff' : '#1a1a2e', fontSize: '18px' }}>
                {selectedEmail.subject}
              </p>
            </div>

            {selectedEmail.recipients_preview && selectedEmail.recipients_preview.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <strong style={{ color: darkMode ? '#a5b4fc' : '#666' }}>
                  Recipients Preview ({selectedEmail.recipients_count} total):
                </strong>
                <div style={{
                  marginTop: '8px',
                  maxHeight: '120px',
                  overflow: 'auto',
                  backgroundColor: darkMode ? '#16213e' : '#f3f4f6',
                  borderRadius: '8px',
                  padding: '12px',
                  fontSize: '13px'
                }}>
                  {selectedEmail.recipients_preview.map((r, idx) => (
                    <span key={idx} style={{ 
                      display: 'inline-block',
                      padding: '4px 8px',
                      margin: '2px',
                      backgroundColor: darkMode ? '#2d3748' : '#e5e7eb',
                      borderRadius: '4px',
                      color: darkMode ? '#fff' : '#1a1a2e'
                    }}>
                      {r.name}
                    </span>
                  ))}
                  {selectedEmail.recipients_count > 20 && (
                    <span style={{ color: darkMode ? '#a5b4fc' : '#666', fontStyle: 'italic' }}>
                      ... and {selectedEmail.recipients_count - 20} more
                    </span>
                  )}
                </div>
              </div>
            )}

            <div style={{ marginBottom: '20px' }}>
              <strong style={{ color: darkMode ? '#a5b4fc' : '#666' }}>Email Content:</strong>
              <div style={{
                marginTop: '8px',
                padding: '0',
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                maxHeight: '600px',
                overflow: 'auto',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <div 
                  dangerouslySetInnerHTML={{ __html: selectedEmail.html_body }}
                  style={{
                    width: '100%',
                    minHeight: '200px'
                  }}
                />
              </div>
            </div>

            {selectedEmail.status === 'sent' && (
              <div style={{ marginBottom: '20px' }}>
                <strong style={{ color: darkMode ? '#a5b4fc' : '#666' }}>Send Results:</strong>
                <p style={{ margin: '4px 0', color: darkMode ? '#fff' : '#1a1a2e' }}>
                  {selectedEmail.sent_count} delivered, {selectedEmail.failed_count} failed
                </p>
                <p style={{ margin: '4px 0', color: darkMode ? '#a5b4fc' : '#666', fontSize: '13px' }}>
                  Sent at: {formatDate(selectedEmail.sent_at)}
                </p>
              </div>
            )}

            {selectedEmail.status === 'rejected' && (
              <div style={{
                padding: '12px 16px',
                backgroundColor: darkMode ? 'rgba(239, 68, 68, 0.1)' : '#fef2f2',
                borderRadius: '8px',
                marginBottom: '20px',
                color: darkMode ? '#f87171' : '#991b1b'
              }}>
                <strong>Rejection Reason:</strong> {selectedEmail.rejection_reason}
              </div>
            )}

            {selectedEmail.status === 'pending_approval' && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', color: darkMode ? '#a5b4fc' : '#666' }}>
                  Rejection Reason (required if rejecting):
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: `1px solid ${darkMode ? '#4a5568' : '#e5e7eb'}`,
                    backgroundColor: darkMode ? '#16213e' : '#fff',
                    color: darkMode ? '#fff' : '#1a1a2e',
                    resize: 'vertical'
                  }}
                  placeholder="e.g., Content doesn't meet guidelines, inappropriate language, etc."
                />
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: 'auto', paddingTop: '20px', borderTop: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedEmail(null);
                  setRejectionReason('');
                }}
              >
                Close
              </button>
              {selectedEmail.status === 'pending_approval' && (
                <>
                  <button
                    className="btn btn-danger"
                    onClick={handleReject}
                    disabled={rejecting || !rejectionReason.trim()}
                  >
                    {rejecting ? 'Rejecting...' : 'Reject'}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleApprove}
                    disabled={approving}
                  >
                    {approving ? 'Approving...' : `Approve & Send to ${selectedEmail.recipients_count} Users`}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperAdminPromotionalApprovals;

